import { Fragment, useEffect, useRef, useState } from "react";

import { api } from "../../api.js";
import { formatEventTimeLocal } from "../../lib/dashboardTime.js";
import { useReportingTimezone } from "../../lib/ReportingTimezoneContext.js";
import type {
  FilterState,
  HealthTelemetryMinuteRow,
  HealthTelemetryMinutesKind,
  HealthTelemetryMinutesQuery,
  HealthTelemetryMinutesResult,
  HealthTelemetrySpeedTestRow,
} from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import { WifiSignalOverTimeChart } from "./WifiSignalOverTimeChart.js";

export type HealthMinuteExplorerTarget = HealthTelemetryMinutesQuery & {
  title: string;
  /** Table · venue label for the Signal & Network Speed chart (wifi kinds). */
  deviceLabel?: string;
};

interface HealthMinuteExplorerProps {
  filters: FilterState;
  target: HealthMinuteExplorerTarget | null;
  onClose: () => void;
}

/** Match analytics-api `wifi-overview` / telemetry-minutes bad rules. */
const WIFI_SIGNAL_BAD_BELOW_DBM = -80;
const WIFI_TX_LINK_BAD_BELOW_MBPS = 5;
const SPEED_DOWNLOAD_BAD_BELOW_MBPS = 25;
const SPEED_UPLOAD_BAD_BELOW_MBPS = 5;

/** Default fits the card without scrolling; matches “12 on Bad Wi-Fi minutes”. */
const DEFAULT_PAGE_SIZE = 12;
const PAGE_SIZE_OPTIONS = [12, 25, 50, 100] as const;
const SPEED_PAGE_SIZE = 11;

function fmtNum(n: number | null, digits = 0): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return digits > 0 ? n.toFixed(digits) : String(Math.round(n));
}

function kindLabel(kind: HealthTelemetryMinutesKind): string {
  if (kind === "wifi_bad") return "Bad Wi-Fi minutes";
  if (kind === "wifi_good") return "Good Wi-Fi minutes";
  return "Heat minutes";
}

/** Sorted page indices for numeric pager buttons (may skip gaps shown as …). */
function pageNumbersForPager(current: number, total: number): number[] {
  if (total <= 1) return [1];
  if (total <= 14) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const want = new Set<number>([1, total, current, current - 1, current + 1, current - 2, current + 2]);
  for (let p = 1; p <= 3; p++) want.add(p);
  for (let p = total - 2; p <= total; p++) want.add(p);
  return [...want].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
}

function wifiFlags(row: HealthTelemetryMinuteRow): {
  connected: boolean;
  rssi: boolean;
  tx: boolean;
} {
  const connected = row.connected === true;
  const signalBad =
    row.connected === false ||
    (row.rssiDbm != null && row.rssiDbm < WIFI_SIGNAL_BAD_BELOW_DBM);
  return {
    connected: row.connected === false,
    rssi: signalBad && row.rssiDbm != null && row.rssiDbm < WIFI_SIGNAL_BAD_BELOW_DBM,
    tx: connected && row.txMbps != null && row.txMbps < WIFI_TX_LINK_BAD_BELOW_MBPS,
  };
}

function valueWithWarn(text: string, warn: boolean, reason: string) {
  if (!warn) return text;
  return (
    <span className="inline-flex items-center gap-0.5">
      <span>{text}</span>
      <span title={reason} aria-label={reason}>
        ⚠️
      </span>
    </span>
  );
}

function SpeedTestPanel({
  tests,
  reportingTz,
}: {
  tests: HealthTelemetrySpeedTestRow[];
  reportingTz: string;
}) {
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [tests]);

  const totalPages = Math.max(1, Math.ceil(tests.length / SPEED_PAGE_SIZE));
  const current = Math.min(page, totalPages - 1);
  const slice = tests.slice(current * SPEED_PAGE_SIZE, (current + 1) * SPEED_PAGE_SIZE);

  return (
    <div className="flex min-h-0 min-w-0 flex-col border-border border-t pt-2 xl:border-t-0 xl:border-l xl:pl-3 xl:pt-0">
      <div className="mb-1 text-[12px] font-semibold text-text-primary">Speed tests</div>
      <p className="mb-1.5 text-[10px] leading-snug text-text-secondary">
        Succeeded network tests in this window. ⚠️ when download &lt; {SPEED_DOWNLOAD_BAD_BELOW_MBPS}{" "}
        or upload &lt; {SPEED_UPLOAD_BAD_BELOW_MBPS} Mbps.
      </p>
      {tests.length === 0 ? (
        <p className="text-[11px] text-text-secondary">No speed tests in this window.</p>
      ) : (
        <>
          <div className="min-h-0 flex-1 overflow-x-auto">
            <table className="w-full border-collapse text-left text-[11px]">
              <thead className="bg-panel-3 text-text-secondary">
                <tr>
                  <th className="px-1.5 py-1 font-medium">When</th>
                  <th
                    className="px-1.5 py-1 font-medium"
                    title={`Download Mbps — ⚠️ under ${SPEED_DOWNLOAD_BAD_BELOW_MBPS}`}
                  >
                    ↓ Mbps
                  </th>
                  <th
                    className="px-1.5 py-1 font-medium"
                    title={`Upload Mbps — ⚠️ under ${SPEED_UPLOAD_BAD_BELOW_MBPS}`}
                  >
                    ↑ Mbps
                  </th>
                </tr>
              </thead>
              <tbody>
                {slice.map((row) => {
                  const downBad =
                    row.downloadMbps != null && row.downloadMbps < SPEED_DOWNLOAD_BAD_BELOW_MBPS;
                  const upBad =
                    row.uploadMbps != null && row.uploadMbps < SPEED_UPLOAD_BAD_BELOW_MBPS;
                  return (
                    <tr key={row.testedAt} className="border-t border-border">
                      <td className="whitespace-nowrap px-1.5 py-1 tabular-nums text-text-primary">
                        {formatEventTimeLocal(row.testedAt, reportingTz)}
                      </td>
                      <td className="px-1.5 py-1 tabular-nums text-text-primary">
                        {valueWithWarn(
                          fmtNum(row.downloadMbps, 1),
                          downBad,
                          `Download under ${SPEED_DOWNLOAD_BAD_BELOW_MBPS} Mbps`,
                        )}
                      </td>
                      <td className="px-1.5 py-1 tabular-nums text-text-primary">
                        {valueWithWarn(
                          fmtNum(row.uploadMbps, 1),
                          upBad,
                          `Upload under ${SPEED_UPLOAD_BAD_BELOW_MBPS} Mbps`,
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {totalPages > 1 ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 border-border-subtle border-t pt-1.5 text-[10px] text-text-secondary">
              <button
                type="button"
                className="rounded border border-border-subtle bg-panel-2 px-1.5 py-0.5 text-text-primary disabled:opacity-40"
                disabled={current <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                Prev
              </button>
              <button
                type="button"
                className="rounded border border-border-subtle bg-panel-2 px-1.5 py-0.5 text-text-primary disabled:opacity-40"
                disabled={current >= totalPages - 1}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              >
                Next
              </button>
              <span className="tabular-nums">
                {current + 1}/{totalPages} · {tests.length} tests
              </span>
            </div>
          ) : (
            <p className="mt-1 text-[10px] text-text-secondary tabular-nums">{tests.length} test(s)</p>
          )}
        </>
      )}
    </div>
  );
}

export function HealthMinuteExplorer({ filters, target, onClose }: HealthMinuteExplorerProps) {
  const reportingTz = useReportingTimezone();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [jumpDraft, setJumpDraft] = useState("1");
  /** Wi-Fi only: when true, list bad minutes (not connected / weak signal / slow TX). */
  const [issuesOnly, setIssuesOnly] = useState(true);
  /** After auto-fallback or a manual toggle, don't keep flipping Issues only. */
  const issuesOnlyLocked = useRef(false);
  const [data, setData] = useState<HealthTelemetryMinutesResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const wifiKind = target?.kind === "wifi_bad" || target?.kind === "wifi_good";
  const effectiveKind: HealthTelemetryMinutesKind | null = !target
    ? null
    : wifiKind
      ? issuesOnly
        ? "wifi_bad"
        : "wifi_good"
      : target.kind;

  useEffect(() => {
    setPage(0);
    setPageSize(DEFAULT_PAGE_SIZE);
    setJumpDraft("1");
    setIssuesOnly(true);
    issuesOnlyLocked.current = false;
  }, [target]);

  useEffect(() => {
    if (!target || !effectiveKind) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setError(null);
    api
      .healthTelemetryMinutes(filters, {
        ...target,
        kind: effectiveKind,
        limit: pageSize,
        offset: page * pageSize,
      })
      .then((next) => {
        if (cancelled) return;
        setData(next);
        // Default Issues only on; if there are no bad minutes, show good instead (once).
        if (
          wifiKind &&
          issuesOnly &&
          !issuesOnlyLocked.current &&
          next.available &&
          next.total === 0
        ) {
          issuesOnlyLocked.current = true;
          setIssuesOnly(false);
          setPage(0);
          setJumpDraft("1");
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setData(null);
          setError(err instanceof Error ? err.message : "Failed to load minutes");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [filters, target, effectiveKind, wifiKind, issuesOnly, page, pageSize]);

  if (!target || !effectiveKind) return null;

  const displayTitle = wifiKind ? kindLabel(effectiveKind) : target.title;
  const total = data?.total ?? 0;
  const limit = data?.limit ?? pageSize;
  const offset = data?.offset ?? page * pageSize;
  const currentPage = Math.floor(offset / Math.max(1, limit)) + 1;
  const totalPages = Math.max(1, Math.ceil(total / Math.max(1, limit)));
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = offset + (data?.rows.length ?? 0);
  const pageNums = pageNumbersForPager(currentPage, totalPages);

  const goToPage = (p: number) => {
    const next = Math.min(totalPages, Math.max(1, p));
    setPage(next - 1);
    setJumpDraft(String(next));
  };

  const telemetryHeader = (
    <div className="mb-1 flex items-baseline justify-between gap-2 text-[11px]">
      <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-semibold text-white">Device Telemetry</span>
        {data?.available ? (
          <span className="tabular-nums text-text-secondary">
            Showing {data.rows.length} of {total} minute(s)
            {data.rows.length > 0 ? ` (${rangeStart}–${rangeEnd} this page)` : ""}
          </span>
        ) : null}
      </div>
      {wifiKind ? (
        <label
          className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 text-text-secondary"
          title="Not connected, or Signal < −80 dBm, or connected Network Speed (TX) under 5 Mbps"
        >
          <input
            type="checkbox"
            className="accent-accent-2"
            checked={issuesOnly}
            onChange={(e) => {
              issuesOnlyLocked.current = true;
              setIssuesOnly(e.target.checked);
              setPage(0);
              setJumpDraft("1");
            }}
          />
          Issues only
        </label>
      ) : null}
    </div>
  );

  const minutesBody =
    !error && data?.available && data.rows.length > 0 ? (
      <>
        {telemetryHeader}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-left text-[12px]">
            <thead className="bg-panel-3 text-text-secondary">
              <tr>
                <th className="px-1.5 py-1 font-medium">When</th>
                <th className="px-1.5 py-1 font-medium">Table</th>
                <th
                  className="px-1.5 py-1 font-medium"
                  title="SoC temperature (°C) from this telemetry minute"
                >
                  °C
                </th>
                <th
                  className="px-1.5 py-1 font-medium"
                  title="Whether the table reported Wi‑Fi as associated/connected in this minute"
                >
                  Connected
                </th>
                <th
                  className="px-1.5 py-1 font-medium"
                  title="Signal — Received Signal Strength Indicator (RSSI, dBm). Higher (closer to 0) is better; we flag below −80 dBm"
                >
                  Signal (RSSI)
                </th>
                <th
                  className="px-1.5 py-1 font-medium"
                  title="Network Speed — negotiated TX link (Mbps). We flag under 5 Mbps while connected"
                >
                  Network Speed (TX)
                </th>
                <th
                  className="px-1.5 py-1 font-medium"
                  title="Receive (download) negotiated link speed in Mbps — shown for context; not used for the good/bad bar"
                >
                  RX Mbps
                </th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => {
                const label = row.venueName
                  ? `${row.deviceName} · ${row.venueName}`
                  : row.deviceName;
                const flags = effectiveKind === "wifi_bad" ? wifiFlags(row) : null;
                const connectedText =
                  row.connected == null ? "—" : row.connected ? "yes" : "no";
                return (
                  <tr key={`${row.deviceId}-${row.minuteStart}`} className="border-t border-border">
                    <td className="px-1.5 py-1 tabular-nums text-text-primary">
                      {formatEventTimeLocal(row.minuteStart, reportingTz)}
                    </td>
                    <td
                      className="max-w-[180px] truncate px-1.5 py-1 text-text-primary"
                      title={label}
                    >
                      {label || "—"}
                    </td>
                    <td className="px-1.5 py-1 tabular-nums text-text-primary">
                      {fmtNum(row.socTempCelsius, 1)}
                    </td>
                    <td className="px-1.5 py-1 text-text-primary">
                      {valueWithWarn(
                        connectedText,
                        Boolean(flags?.connected),
                        "Not connected",
                      )}
                    </td>
                    <td className="px-1.5 py-1 tabular-nums text-text-primary">
                      {valueWithWarn(
                        fmtNum(row.rssiDbm),
                        Boolean(flags?.rssi),
                        `Bad Signal ≤ ${WIFI_SIGNAL_BAD_BELOW_DBM} dBm`,
                      )}
                    </td>
                    <td className="px-1.5 py-1 tabular-nums text-text-primary">
                      {valueWithWarn(
                        fmtNum(row.txMbps, 1),
                        Boolean(flags?.tx),
                        `Bad Network Speed < ${WIFI_TX_LINK_BAD_BELOW_MBPS} Mbps while connected`,
                      )}
                    </td>
                    <td className="px-1.5 py-1 tabular-nums text-text-primary">
                      {fmtNum(row.rxMbps, 1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-border-subtle pt-1.5 text-[11px] text-text-secondary">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded border border-border-subtle bg-panel-2 px-2 py-1 text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              Next
            </button>
            <span className="tabular-nums">
              Page {currentPage} of {totalPages}
            </span>
            <label className="inline-flex items-center gap-1">
              <span className="text-text-secondary">Rows/page</span>
              <select
                className="rounded border border-border-subtle bg-panel-2 px-1 py-0.5 text-[11px] text-text-primary"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number.parseInt(e.target.value, 10));
                  setPage(0);
                  setJumpDraft("1");
                }}
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex max-w-[min(100vw-24px,520px)] flex-wrap items-center gap-1 overflow-x-auto py-0.5">
              {pageNums.map((p, i) => (
                <Fragment key={p}>
                  {i > 0 && pageNums[i - 1] !== undefined && p > pageNums[i - 1]! + 1 ? (
                    <span className="px-0.5 text-text-secondary" aria-hidden>
                      …
                    </span>
                  ) : null}
                  <button
                    type="button"
                    className={`min-w-[1.5rem] rounded border px-1.5 py-0.5 tabular-nums ${
                      p === currentPage
                        ? "border-accent-2 bg-panel-3 font-semibold text-text-primary"
                        : "border-border-subtle bg-panel-2 text-text-primary hover:bg-[#223455]"
                    }`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                </Fragment>
              ))}
            </div>
            <span className="inline-flex items-center gap-1 border-border-subtle border-l pl-2">
              <span>Go to</span>
              <input
                type="number"
                min={1}
                max={totalPages}
                className="w-11 rounded border border-border-subtle bg-panel-2 px-1 py-0.5 text-[11px] text-text-primary tabular-nums"
                value={jumpDraft}
                onChange={(e) => setJumpDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  const n = Number.parseInt(jumpDraft, 10);
                  if (Number.isFinite(n)) goToPage(n);
                }}
              />
              <button
                type="button"
                className="rounded border border-border-subtle bg-panel-2 px-1.5 py-0.5 text-text-primary"
                onClick={() => {
                  const n = Number.parseInt(jumpDraft, 10);
                  if (Number.isFinite(n)) goToPage(n);
                }}
              >
                Go
              </button>
            </span>
          </div>
        </div>
      </>
    ) : null;

  return (
    <div className="xl:col-span-2">
      <ChartCard
        compact
        title={displayTitle}
        criteria={`Source: device_telemetry_minutes (not Raw Events / device_events).

${
  effectiveKind === "wifi_bad"
    ? "Kind: bad Wi-Fi minutes — not connected, or Signal (rssi_dbm) < −80, or connected with Network Speed (TX link) under 5 Mbps (RX ignored). ⚠️ marks the column(s) that failed those checks. **Issues only** defaults on; if there are no bad minutes it turns off and shows good. Above the table: Signal & Network Speed over time (fixed −100…0 dBm / 0…100 Mbps; dashed Bad Signal ≤ −80 dBm / Bad Network Speed < 5 Mbps). Speed tests sit in the side panel (paginated; ⚠️ if download < 25 Mbps or upload < 5 Mbps)."
    : effectiveKind === "wifi_good"
      ? "Kind: good Wi-Fi minutes — sampled minutes with wifi present that are not bad (connected, Signal ≥ −80 or missing, and Network Speed ≥ 5 Mbps when connected; RX ignored). **Issues only** defaults on when bad minutes exist. Above the table: Signal & Network Speed over time. Speed tests sit in the side panel (paginated; ⚠️ if download < 25 Mbps or upload < 5 Mbps)."
      : `Kind: heat band minutes — soc_temp_celsius in the clicked band. Heat cutover ${data?.cutoverIso ?? "2026-10-13T00:00:00.000Z"} (UTC): earlier minutes are omitted. Sleep minutes are included.`
}

Game filter does not apply. Columns are the minute row fields (when, °C, connected, Signal, Network Speed, RX).`}
        note={
          effectiveKind === "wifi_bad"
            ? "Signal & Network Speed chart above, bad Wi-Fi minutes below, speed tests on the right. Not Raw Events."
            : effectiveKind === "wifi_good"
              ? "Signal & Network Speed chart above, good Wi-Fi minutes below, speed tests on the right. Not Raw Events."
              : `${kindLabel(effectiveKind)} from telemetry — not the Raw Events log.`
        }
        titleRight={
          <button
            type="button"
            className="rounded border border-border px-2 py-0.5 text-[12px] text-text-secondary hover:bg-panel-2 hover:text-text-primary"
            onClick={onClose}
          >
            Close
          </button>
        }
      >
        {error ? <p className="text-xs text-err">{error}</p> : null}
        {!error && !data ? <p className="text-xs text-text-secondary">Loading minutes…</p> : null}
        {!error && data && !data.available ? (
          <p className="text-xs text-text-secondary">
            Telemetry minutes are not in this database yet (`device_telemetry_minutes`).
          </p>
        ) : null}
        {wifiKind && target.deviceId ? (
          <>
            <WifiSignalOverTimeChart
              filters={filters}
              deviceId={target.deviceId}
              deviceLabel={target.deviceLabel ?? target.title}
            />
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(200px,30%)]">
              <div className="min-w-0">
                {!error && data?.available && data.rows.length === 0 ? (
                  <>
                    {telemetryHeader}
                    <p className="text-xs text-text-secondary">No matching minutes in this window.</p>
                  </>
                ) : (
                  minutesBody
                )}
              </div>
              {!error && data?.available ? (
                <SpeedTestPanel tests={data.speedTests} reportingTz={reportingTz} />
              ) : null}
            </div>
          </>
        ) : !error && data?.available && data.rows.length === 0 ? (
          <p className="text-xs text-text-secondary">No matching minutes in this window.</p>
        ) : (
          minutesBody
        )}
      </ChartCard>
    </div>
  );
}
